import { Helmet } from "react-helmet-async";
import { BRAND } from "@/data/siteData";

/**
 * SEO component — sets <title>, meta tags, Open Graph, Twitter, JSON-LD per page.
 * Pass `path` (e.g. "/modeli/lunar") so canonical URL is built.
 */
export default function SEO({
    title,
    description,
    path = "",
    image,
    type = "website",
    jsonLd,
    keywords,
}) {
    const fullTitle = title
        ? `${title} | ${BRAND.name}`
        : `${BRAND.name} | Aluminijske ograde po mjeri`;
    const desc = description || BRAND.description;
    const url = `${BRAND.siteUrl}${path}`;
    const ogImage =
        image ||
        "https://www.smart-mak.com/wp-content/uploads/2026/02/aluminium-premium-zaunsystem-mit-vertikaler-linienfuehrung-galactic-700-800px.png";

    return (
        <Helmet>
            <html lang="bs" />
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={url} />

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={BRAND.name} />
            <meta property="og:locale" content="bs_BA" />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={ogImage} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={ogImage} />

            {/* Robots */}
            <meta name="robots" content="index,follow,max-image-preview:large" />
            <meta name="googlebot" content="index,follow" />

            {jsonLd && (
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            )}
        </Helmet>
    );
}

// Common JSON-LD generators
export const localBusinessJsonLd = () => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${BRAND.siteUrl}/#business`,
    name: BRAND.name,
    description: BRAND.description,
    url: BRAND.siteUrl,
    telephone: BRAND.phone,
    email: BRAND.email,
    image:
        "https://www.smart-mak.com/wp-content/uploads/2026/02/premium-alu-zaun-und-toranlage-orbit.jpg",
    address: {
        "@type": "PostalAddress",
        streetAddress: BRAND.streetAddress,
        addressLocality: BRAND.addressLocality,
        postalCode: BRAND.postalCode,
        addressCountry: BRAND.addressCountry,
    },
    openingHoursSpecification: [
        {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opens: "08:00",
            closes: "17:00",
        },
    ],
    areaServed: {
        "@type": "Country",
        name: "Bosna i Hercegovina",
    },
    priceRange: "$$",
});

export const productJsonLd = (model) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    name: `Aluminijska ograda ${model.name}`,
    image: model.img,
    description: model.long || model.desc,
    brand: { "@type": "Brand", name: BRAND.name },
    category: "Aluminijske ograde",
    offers: {
        "@type": "Offer",
        availability: "https://schema.org/InStock",
        priceCurrency: "BAM",
        seller: { "@type": "Organization", name: BRAND.name },
        url: `${BRAND.siteUrl}/modeli/${model.id}`,
    },
});

export const breadcrumbJsonLd = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        item: `${BRAND.siteUrl}${it.path}`,
    })),
});

export const faqJsonLd = (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
});
